import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, CheckCircle2, XCircle, Clock, PoundSterling, RotateCcw } from "lucide-react";
import { API } from "@/contexts/AuthContext";

/**
 * Per-defect FOH decision row (MOT-3).
 *
 * Compact action strip rendered inside the MOT Outcome Summary for every
 * actionable defect (i.e. severity != "user_entered"). One-tap to record:
 *
 *   • Priced     — FOH has put this on a yestimate (or quoted verbally)
 *   • Authorised — customer has explicitly authorised the work
 *   • Declined   — customer has explicitly declined
 *   • Deferred   — customer wants to think / book back in
 *   • Cleared    — revoke the previous decision (audit trail kept)
 *
 * Reason field is optional except for declined / deferred where it surfaces
 * inline. Quoted price is optional and only displayed for "priced".
 *
 * Architectural rules respected:
 *   • The defect text / severity are NEVER editable here — display only.
 *   • The decision is recorded server-side as an append-only entry.
 *   • Declined / deferred decisions mirror into service_history so MOT-1's
 *     previous_declines surface picks them up on the next visit.
 */
export default function MOTDefectDecisionRow({
  sessionId,
  outcomeId,
  defectIndex,
  defect,
  currentDecision,
  onChange,
}) {
  const [busyKind, setBusyKind] = useState(null);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [reason, setReason] = useState(currentDecision?.reason || "");
  const [priceGbp, setPriceGbp] = useState(
    currentDecision?.quoted_price_pence != null
      ? (currentDecision.quoted_price_pence / 100).toFixed(2)
      : ""
  );

  const submit = async (decision, opts = {}) => {
    setBusyKind(decision);
    setError(null);
    try {
      const body = {
        decision,
        reason: opts.reason ?? reason?.trim() ?? null,
      };
      if (decision === "priced") {
        const pence = priceGbp
          ? Math.round(parseFloat(priceGbp) * 100)
          : null;
        if (pence != null && !Number.isNaN(pence)) {
          body.quoted_price_pence = pence;
        }
      }
      const r = await axios.post(
        `${API}/intake/sessions/${sessionId}/mot-outcome/defects/${defectIndex}/decision`,
        body
      );
      onChange?.(r.data);
      // Auto-collapse the reason input after a successful submission so
      // the row stays compact.
      setExpanded(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not record decision");
    } finally {
      setBusyKind(null);
    }
  };

  const isActioned = !!currentDecision && currentDecision.decision !== "cleared";
  const currentKind = currentDecision?.decision;

  return (
    <div
      className="mt-2 pt-2 border-t border-slate-100 space-y-2"
      data-testid={`mot-defect-decision-row-${defectIndex}`}
    >
      {/* Status strip */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 flex-wrap min-w-0">
          {isActioned ? (
            <CurrentDecisionPill
              decision={currentKind}
              label={currentDecision?.decision_label}
              by={currentDecision?.decided_by_name}
              at={currentDecision?.decided_at}
              price={currentDecision?.quoted_price_pence}
            />
          ) : (
            <span
              className="text-[11.5px] uppercase tracking-wide bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5 font-semibold"
              data-testid={`mot-defect-unactioned-${defectIndex}`}
            >
              Awaiting decision
            </span>
          )}
        </div>

        {/* Action buttons — compact one-tap */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <DecisionBtn
            kind="priced"
            icon={<PoundSterling className="w-3 h-3" />}
            label="Price"
            active={currentKind === "priced"}
            busy={busyKind === "priced"}
            disabled={!!busyKind}
            onClick={() => setExpanded((v) => v !== "priced" ? "priced" : false)}
            testid={`mot-defect-price-btn-${defectIndex}`}
          />
          <DecisionBtn
            kind="authorised"
            icon={<CheckCircle2 className="w-3 h-3" />}
            label="Authorised"
            active={currentKind === "authorised"}
            busy={busyKind === "authorised"}
            disabled={!!busyKind}
            onClick={() => submit("authorised", { reason: null })}
            testid={`mot-defect-authorise-btn-${defectIndex}`}
          />
          <DecisionBtn
            kind="declined"
            icon={<XCircle className="w-3 h-3" />}
            label="Declined"
            active={currentKind === "declined"}
            busy={busyKind === "declined"}
            disabled={!!busyKind}
            onClick={() => setExpanded((v) => v !== "declined" ? "declined" : false)}
            testid={`mot-defect-decline-btn-${defectIndex}`}
          />
          <DecisionBtn
            kind="deferred"
            icon={<Clock className="w-3 h-3" />}
            label="Defer"
            active={currentKind === "deferred"}
            busy={busyKind === "deferred"}
            disabled={!!busyKind}
            onClick={() => setExpanded((v) => v !== "deferred" ? "deferred" : false)}
            testid={`mot-defect-defer-btn-${defectIndex}`}
          />
          {isActioned && (
            <DecisionBtn
              kind="cleared"
              icon={<RotateCcw className="w-3 h-3" />}
              label="Clear"
              active={false}
              busy={busyKind === "cleared"}
              disabled={!!busyKind}
              onClick={() => submit("cleared", { reason: null })}
              testid={`mot-defect-clear-btn-${defectIndex}`}
            />
          )}
        </div>
      </div>

      {/* Inline expansion (price / reason) */}
      {expanded === "priced" && (
        <InlinePanel
          label="Quoted price (£, optional)"
          actionLabel="Mark as priced"
          busy={busyKind === "priced"}
          onSubmit={() => submit("priced")}
          onCancel={() => setExpanded(false)}
          testid={`mot-defect-price-panel-${defectIndex}`}
        >
          <div className="flex items-center gap-2">
            <span className="text-slate-500 text-[13px]">£</span>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={priceGbp}
              onChange={(e) => setPriceGbp(e.target.value)}
              placeholder="0.00"
              className="text-[13.5px] h-8 w-28"
              data-testid={`mot-defect-price-input-${defectIndex}`}
            />
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Note / yestimate ref (optional)"
              className="text-[13.5px] h-8 flex-1"
              data-testid={`mot-defect-price-note-${defectIndex}`}
            />
          </div>
        </InlinePanel>
      )}

      {(expanded === "declined" || expanded === "deferred") && (
        <InlinePanel
          label={`Reason — what did the customer say? (${expanded === "declined" ? "declined" : "deferred"})`}
          actionLabel={expanded === "declined" ? "Mark as declined" : "Mark as deferred"}
          busy={busyKind === expanded}
          onSubmit={() => submit(expanded)}
          onCancel={() => setExpanded(false)}
          testid={`mot-defect-${expanded}-panel-${defectIndex}`}
        >
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. customer wants a second opinion"
            className="text-[13.5px] h-8"
            data-testid={`mot-defect-${expanded}-reason-${defectIndex}`}
          />
        </InlinePanel>
      )}

      {error && (
        <p
          className="text-[12.5px] text-red-700 italic"
          data-testid={`mot-defect-decision-error-${defectIndex}`}
        >
          {error}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function DecisionBtn({ icon, label, active, busy, disabled, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-1 text-[12px] font-semibold border rounded-md h-7 px-2 transition-colors ${
        active
          ? "bg-slate-900 text-white border-slate-900"
          : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
      } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
      data-testid={testid}
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : icon}
      <span>{label}</span>
    </button>
  );
}

function InlinePanel({ label, actionLabel, busy, onSubmit, onCancel, testid, children }) {
  return (
    <div
      className="rounded-md border border-slate-200 bg-slate-50/50 px-3 py-2.5 space-y-2"
      data-testid={testid}
    >
      <p className="text-[11.5px] uppercase tracking-wide text-slate-500 font-semibold">
        {label}
      </p>
      {children}
      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={busy}
          className="h-7 text-[12.5px]"
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={onSubmit}
          disabled={busy}
          className="bg-slate-900 hover:bg-slate-800 text-white h-7 text-[12.5px]"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : null}
          {actionLabel}
        </Button>
      </div>
    </div>
  );
}

function CurrentDecisionPill({ decision, label, by, at, price }) {
  const tones = {
    priced:      "bg-blue-50 text-blue-800 border-blue-200",
    authorised:  "bg-emerald-50 text-emerald-800 border-emerald-200",
    declined:    "bg-red-50 text-red-800 border-red-200",
    deferred:    "bg-slate-100 text-slate-700 border-slate-200",
    cleared:     "bg-white text-slate-500 border-slate-200",
  };
  const tone = tones[decision] || tones.cleared;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[11.5px] uppercase tracking-wide font-semibold border rounded-full px-2 py-0.5 ${tone}`}
      data-testid={`mot-defect-current-decision-${decision}`}
    >
      <span>{label || decision}</span>
      {price != null && decision === "priced" && (
        <span className="font-mono normal-case">· £{(price / 100).toFixed(2)}</span>
      )}
      {by && (
        <span className="normal-case font-normal text-slate-500 ml-0.5">
          · {by}{at ? ` · ${formatRelative(at)}` : ""}
        </span>
      )}
    </span>
  );
}

function formatRelative(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  } catch {
    return "";
  }
}
